import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { DataTable, DataRow } from "@/components/ui/data-table"
import { GradeBadge } from "@/components/ui/grade-badge"
import { FindingCard } from "@/components/ui/finding-card"

type Check = {
  key: string
  label: string
  status: "pass" | "warn" | "fail"
  finding: string
  detail?: string
}

type Rank = {
  keyword: string
  label: string
  position: number | null
  result_count: number
  thin: boolean
  checked: boolean
  winner?: { name: string; author: string; title_contains_keyword: boolean }
}

type Results = {
  fetched_at?: string
  feed_url?: string | null
  final_url?: string | null
  feed_truncated?: boolean | null
  apple?: {
    state: string
    collection_id: number | null
    identity_match: string | null
    duplicate_ids: number[] | null
  }
  keyword_source?: string | null
  keywords_extra?: string[]
  ranks?: Rank[]
  method?: { checked: string[]; not_checked: string[] }
  checks?: Check[]
}

type Audit = {
  id: number
  token: string
  show_title: string | null
  input_url: string
  email: string
  status: string
  grade: string | null
  score: number | null
  created_at: string
  referrer: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  apple_state: string | null
  kit_synced: boolean
  kit_subscriber_id: string | null
  kit_synced_at: string | null
  kit_sync_error: string | null
  results: Results
}

const STATUS_ORDER = { fail: 0, warn: 1, pass: 2 } as const

export default function AdminPodcastAuditShow({ audit }: { audit: Audit }) {
  const checks = [...(audit.results.checks ?? [])].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
  const ranks = audit.results.ranks ?? []
  const apple = audit.results.apple

  return (
    <>
      <Head title={`Podcast audit — ${audit.show_title || audit.input_url}`}>
        <meta name="description" content="Podcast discoverability audit lead detail." />
        <meta property="og:title" content={`Podcast audit — ${audit.show_title || audit.input_url}`} />
        <meta property="og:description" content="Podcast discoverability audit lead detail." />
      </Head>
      <AdminShell>
        <Link
          href="/admin/podcast_audits"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ArrowLeft className="h-4 w-4" /> All podcast audits
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4 border-b border-hairline pb-6">
          <div className="flex items-start gap-4">
            {audit.grade && <GradeBadge grade={audit.grade} size="md" />}
            <div>
              <h1>{audit.show_title || audit.input_url}</h1>
              {audit.show_title && <p className="mt-1 text-sm text-ink-muted">{audit.input_url}</p>}
              <p className="mt-1">
                <a href={`mailto:${audit.email}`} className="font-medium text-accent-display">
                  {audit.email}
                </a>
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                Ran {formatDate(audit.created_at)}
                {audit.utm_source && ` · via ${audit.utm_source}`}
                {audit.referrer && ` · from ${audit.referrer}`}
              </p>
            </div>
          </div>
          <div className="text-right">
            {audit.score !== null && <p className="text-sm text-ink-muted">{audit.score}/100</p>}
            <a
              href={`/podcast-audit/${audit.token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-sm text-ink-body no-underline hover:text-accent-display"
            >
              Public report <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {checks.length === 0 ? (
          <p className="mt-8 text-ink-muted">No results yet — status: {audit.status}.</p>
        ) : (
          <>
            <div className="mt-2">
              <h2>Findings ({checks.length})</h2>
              <div className="mt-4 space-y-4">
                {checks.map((check) => (
                  <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
                ))}
              </div>
            </div>

            {ranks.length > 0 && (
              <div className="mt-10">
                <h2>Keyword ranks</h2>
                <div className="mt-4 overflow-x-auto rounded-xl border border-hairline">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-hairline bg-surface text-left">
                        <th className="px-4 py-3 font-medium text-ink-muted">Keyword</th>
                        <th className="px-4 py-3 font-medium text-ink-muted">Rank</th>
                        <th className="px-4 py-3 font-medium text-ink-muted">Top result (if unranked)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline">
                      {ranks.map((rank) => (
                        <tr key={rank.keyword}>
                          <td className="px-4 py-3 font-medium text-ink-display">{rank.keyword}</td>
                          <td className="px-4 py-3 text-ink-body">
                            {rank.label}
                            {rank.thin && <span className="ml-2 text-xs text-ink-muted">(small pool)</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-ink-muted">
                            {rank.winner
                              ? `${rank.winner.name} by ${rank.winner.author}${
                                  rank.winner.title_contains_keyword ? " (keyword in title)" : ""
                                }`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-10">
          <h2>Diagnostics</h2>
          <div className="mt-4">
            <DataTable>
              <DataRow title="Apple state">
                {apple?.state ? <Badge tone="neutral">{apple.state}</Badge> : "—"}
              </DataRow>
              <DataRow title="Identity match">{apple?.identity_match || "—"}</DataRow>
              <DataRow title="Collection id">{apple?.collection_id ?? "—"}</DataRow>
              <DataRow title="Duplicate ids">
                {apple?.duplicate_ids && apple.duplicate_ids.length > 0 ? apple.duplicate_ids.join(", ") : "—"}
              </DataRow>
              <DataRow title="Keyword source">{audit.results.keyword_source || "—"}</DataRow>
              <DataRow title="Feed truncated">{audit.results.feed_truncated ? "Yes" : "No"}</DataRow>
              <DataRow title="Feed URL">{audit.results.feed_url || "—"}</DataRow>
              <DataRow title="Final URL (after redirects)">{audit.results.final_url || "—"}</DataRow>
              <DataRow title="Kit sync">
                {audit.kit_synced ? (
                  <Badge tone="signal">Synced {audit.kit_synced_at && formatDate(audit.kit_synced_at)}</Badge>
                ) : audit.kit_sync_error ? (
                  <Badge tone="accent">Error: {audit.kit_sync_error}</Badge>
                ) : (
                  <Badge tone="muted">Not synced</Badge>
                )}
              </DataRow>
              <DataRow title="UTM">
                {[audit.utm_source, audit.utm_medium, audit.utm_campaign].filter(Boolean).join(" / ") || "—"}
              </DataRow>
            </DataTable>
          </div>
        </div>

        <div className="mt-10">
          <details>
            <summary className="cursor-pointer text-sm font-medium text-ink-display">Raw results JSON</summary>
            <pre className="mt-3 overflow-x-auto rounded-lg border border-hairline bg-surface p-4 text-xs text-ink-body">
              {JSON.stringify(audit.results, null, 2)}
            </pre>
          </details>
        </div>
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
